import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, MessageCircleQuestion } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { AI_ADDONS } from "../../../constants/pricing.constants";
import cn from "../../../lib/cn";

const aiAddonPricing = AI_ADDONS.map(
  (addon) => `${addon.name} at ৳${addon.price} per ${addon.unit}`,
).join(" and ");

const FAQS: { question: string; answer: ReactNode }[] = [
  {
    question: "What is HRX?",
    answer:
      "HRX is a cloud-based, AI-powered HRM platform that brings attendance, leave, shifts, payroll, recruitment and performance into one place — with dashboards that show owners and managers what is happening across the organization.",
  },
  {
    question: "Which plan is right for my team?",
    answer:
      "Plans are sized by team and modules. Basic covers attendance, leave and shift management; Standard adds payroll; Premium adds recruitment and performance management. Each plan card shows its user limit. If you need custom modules or more users, Enterprise is built for you.",
  },
  {
    question: "How do the AI add-ons work?",
    answer: `AI add-ons are pay-per-use and are not included in the plan price. They are available on plans with recruitment (Premium and Enterprise): ${aiAddonPricing}.`,
  },
  {
    question: "Can I pay monthly or yearly?",
    answer:
      "Yes. Use the Monthly / Yearly switch in the pricing section to see the price for each billing cycle, then choose the plan that suits you.",
  },
  {
    question: "How do I get started?",
    answer:
      "Pick a plan, verify your email with a one-time code, and set up your company — branches, departments and employees. Prefer a walkthrough? Request a demo and our team will guide you.",
  },
  {
    question: "How does attendance tracking work?",
    answer:
      "Employees check in with a selfie inside a geo-fenced area, so you know who is present, where and when. Late arrivals and attendance health are visible on the dashboard.",
  },
  {
    question: "What does the Enterprise plan include?",
    answer:
      "Everything in Premium, plus custom modules built around your workflow and API integration with your existing systems. Pricing is tailored to your organization — contact our team to discuss your needs.",
  },
  {
    question: "Which payment methods do you accept?",
    answer:
      "Payments are processed securely through SSLCOMMERZ, which supports major cards and popular mobile banking options in Bangladesh.",
  },
];

const FaqItem = ({
  question,
  answer,
  index,
  open,
  onToggle,
}: {
  question: string;
  answer: ReactNode;
  index: number;
  open: boolean;
  onToggle: () => void;
}) => {
  const id = `faq-${index}`;
  return (
    <div className="relative">
      {/* Accent bar on the open item */}
      <span
        className={cn(
          "absolute inset-y-0 left-0 w-0.5 bg-theme-primary transition-opacity duration-300",
          open ? "opacity-100" : "opacity-0",
        )}
      />
      <button
        id={`${id}-button`}
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        onClick={onToggle}
        className="group flex w-full items-center justify-between gap-6 px-6 py-5 text-left sm:px-8 sm:py-6"
      >
        <span
          className={cn(
            "text-base font-semibold transition-colors sm:text-[17px]",
            open ? "text-gray-900" : "text-gray-700 group-hover:text-gray-900",
          )}
        >
          {question}
        </span>
        {/* Plus that morphs into a minus */}
        <span
          className={cn(
            "relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors duration-300",
            open
              ? "bg-theme-primary text-white"
              : "bg-gray-100 text-gray-500 group-hover:bg-gray-200",
          )}
        >
          <span className="absolute h-[1.5px] w-3 rounded-full bg-current" />
          <span
            className={cn(
              "absolute h-3 w-[1.5px] rounded-full bg-current transition-transform duration-300",
              open ? "scale-y-0" : "scale-y-100",
            )}
          />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`${id}-panel`}
            role="region"
            aria-labelledby={`${id}-button`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="-mt-1 pb-6 pl-6 pr-14 text-[15px] leading-relaxed text-gray-500 sm:pb-7 sm:pl-8 sm:pr-20">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ContactCard = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 p-6",
      className,
    )}
  >
    <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-theme-primary/15 blur-3xl" />
    <span className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-white text-theme-primary ring-1 ring-gray-200">
      <MessageCircleQuestion className="h-5 w-5" strokeWidth={1.75} />
    </span>
    <h3 className="relative mt-4 font-semibold text-gray-900">
      Still have questions?
    </h3>
    <p className="relative mt-1 text-sm leading-relaxed text-gray-500">
      Can't find what you're looking for? Our team is happy to help.
    </p>
    <Link
      to="/contact"
      className="relative mt-4 inline-flex items-center gap-1 text-sm font-semibold text-theme-primary hover:underline hover:underline-offset-4"
    >
      Contact us
      <ArrowUpRight className="h-4 w-4" />
    </Link>
  </div>
);

// `className` / `containerClassName` let other pages (e.g. Contact) adjust
// spacing and width while keeping the home page defaults.
const Faq = ({
  className = "pb-24 pt-4 sm:pb-28",
  containerClassName = "max-w-7xl",
  showContactCard = true,
}: {
  className?: string;
  containerClassName?: string;
  showContactCard?: boolean;
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className={cn("bg-white px-4 sm:px-6 lg:px-8", className)}>
      <div
        className={cn(
          "mx-auto grid gap-12 lg:grid-cols-12 lg:gap-16",
          containerClassName,
        )}
      >
        {/* Intro (sticky on desktop) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-4"
        >
          <div className="lg:sticky lg:top-28">
            <p className="text-sm font-semibold uppercase tracking-wider text-theme-primary">
              FAQ
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Frequently asked questions
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-gray-500">
              Everything you need to know about HRX, plans and billing.
            </p>

            {showContactCard && (
              <ContactCard className="mt-10 hidden lg:block" />
            )}
          </div>
        </motion.div>

        {/* Questions */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-8"
        >
          <div className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-200 bg-white">
            {FAQS.map((faq, index) => (
              <FaqItem
                key={faq.question}
                index={index}
                question={faq.question}
                answer={faq.answer}
                open={openIndex === index}
                onToggle={() =>
                  setOpenIndex((current) => (current === index ? null : index))
                }
              />
            ))}
          </div>
          {showContactCard && <ContactCard className="mt-8 lg:hidden" />}
        </motion.div>
      </div>
    </section>
  );
};

export default Faq;
